import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  CogIcon,
  BellIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import VendorLayout from '../../components/layout/VendorLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import api from '../../services/api';
import toast from 'react-hot-toast';

const Settings = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('general');

  const { data: profile, isLoading } = useQuery(
    'vendor-profile',
    () => api.get('/vendors/profile').then(res => res.data.data)
  );

  const { data: settingsData, isLoading: settingsLoading } = useQuery(
    'vendor-settings',
    () => api.get('/vendors/settings').then(res => res.data.data)
  );

  const [settings, setSettings] = useState({
    // General Settings
    timezone: 'Africa/Khartoum',
    language: 'ar',
    currency: 'SDG',
    
    // Notification Settings
    emailNotifications: true,
    orderNotifications: true,
    reviewNotifications: true,
    lowStockAlerts: true,
    lowStockThreshold: 5,
    
    // Store Settings
    autoApproveOrders: false,
    allowReviews: true,
    showPhone: true,
    showEmail: true,
    showAddress: true,
    
    // Payment Settings
    acceptCashOnDelivery: true,
    acceptBankTransfer: true,
    bankName: '',
    accountNumber: '',
    accountName: '',
  });

  // Update settings when data is loaded
  React.useEffect(() => {
    if (settingsData) {
      setSettings(settingsData);
    }
  }, [settingsData]);

  const updateSettingsMutation = useMutation(
    (data) => api.put('/vendors/settings', data),
    {
      onSuccess: () => {
        toast.success('تم حفظ الإعدادات بنجاح');
        queryClient.invalidateQueries('vendor-settings');
        queryClient.invalidateQueries('vendor-profile');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'حدث خطأ في حفظ الإعدادات');
      }
    }
  );

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate(settings);
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (isLoading || settingsLoading) {
    return (
      <VendorLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </VendorLayout>
    );
  }

  const tabs = [
    { id: 'general', name: 'عام', icon: CogIcon },
    { id: 'notifications', name: 'الإشعارات', icon: BellIcon },
    { id: 'store', name: 'المتجر', icon: GlobeAltIcon },
    { id: 'payment', name: 'الدفع', icon: CreditCardIcon },
    { id: 'security', name: 'الأمان', icon: ShieldCheckIcon },
  ];

  return (
    <VendorLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">الإعدادات</h1>
          <button
            onClick={handleSaveSettings}
            disabled={updateSettingsMutation.isLoading}
            className="btn-primary"
          >
            {updateSettingsMutation.isLoading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
          </button>
        </div>

        <div className="bg-white rounded-lg shadow">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* General Settings */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900">الإعدادات العامة</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">المنطقة الزمنية</label>
                    <select
                      value={settings.timezone}
                      onChange={(e) => handleChange('timezone', e.target.value)}
                      className="form-input"
                    >
                      <option value="Africa/Khartoum">الخرطوم (GMT+2)</option>
                      <option value="Africa/Cairo">القاهرة (GMT+2)</option>
                      <option value="Asia/Riyadh">الرياض (GMT+3)</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">اللغة</label>
                    <select
                      value={settings.language}
                      onChange={(e) => handleChange('language', e.target.value)}
                      className="form-input"
                    >
                      <option value="ar">العربية</option>
                      <option value="en">English</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">العملة</label>
                    <select
                      value={settings.currency}
                      onChange={(e) => handleChange('currency', e.target.value)}
                      className="form-input"
                    >
                      <option value="SDG">جنيه سوداني (SDG)</option>
                      <option value="USD">دولار أمريكي (USD)</option>
                      <option value="EUR">يورو (EUR)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Notification Settings */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900">إعدادات الإشعارات</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">إشعارات البريد الإلكتروني</h3>
                      <p className="text-sm text-gray-500">استقبال الإشعارات عبر البريد الإلكتروني</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications}
                        onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">إشعارات الطلبات</h3>
                      <p className="text-sm text-gray-500">إشعار عند استلام طلب جديد</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.orderNotifications}
                        onChange={(e) => handleChange('orderNotifications', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">إشعارات التقييمات</h3>
                      <p className="text-sm text-gray-500">إشعار عند استلام تقييم جديد</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.reviewNotifications}
                        onChange={(e) => handleChange('reviewNotifications', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">تنبيهات نفاد المخزون</h3>
                      <p className="text-sm text-gray-500">إشعار عند انخفاض كمية المنتج</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.lowStockAlerts}
                        onChange={(e) => handleChange('lowStockAlerts', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {settings.lowStockAlerts && (
                    <div>
                      <label className="form-label">حد التنبيه (الكمية)</label>
                      <input
                        type="number"
                        value={settings.lowStockThreshold}
                        onChange={(e) => handleChange('lowStockThreshold', parseInt(e.target.value))}
                        className="form-input max-w-xs"
                        min="1"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        سيتم إرسال تنبيه عندما تصل الكمية إلى هذا الحد أو أقل
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Store Settings */}
            {activeTab === 'store' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900">إعدادات المتجر</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">الموافقة التلقائية على الطلبات</h3>
                      <p className="text-sm text-gray-500">قبول الطلبات تلقائياً دون مراجعة</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.autoApproveOrders}
                        onChange={(e) => handleChange('autoApproveOrders', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">السماح بالتقييمات</h3>
                      <p className="text-sm text-gray-500">السماح للعملاء بتقييم المنتجات والمتجر</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.allowReviews}
                        onChange={(e) => handleChange('allowReviews', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h3 className="font-medium text-gray-900 mb-4">معلومات الاتصال المعروضة</h3>
                    
                    <div className="space-y-3">
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={settings.showPhone}
                          onChange={(e) => handleChange('showPhone', e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-gray-700">عرض رقم الهاتف</span>
                      </label>

                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={settings.showEmail}
                          onChange={(e) => handleChange('showEmail', e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-gray-700">عرض البريد الإلكتروني</span>
                      </label>

                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={settings.showAddress}
                          onChange={(e) => handleChange('showAddress', e.target.checked)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-gray-700">عرض العنوان</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Settings */}
            {activeTab === 'payment' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900">إعدادات الدفع</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">الدفع عند الاستلام</h3>
                      <p className="text-sm text-gray-500">قبول الدفع النقدي عند التوصيل</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.acceptCashOnDelivery}
                        onChange={(e) => handleChange('acceptCashOnDelivery', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">التحويل البنكي</h3>
                      <p className="text-sm text-gray-500">قبول الدفع عبر التحويل البنكي</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.acceptBankTransfer}
                        onChange={(e) => handleChange('acceptBankTransfer', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {settings.acceptBankTransfer && (
                    <div className="border-t border-gray-200 pt-4 mt-4 space-y-4">
                      <h3 className="font-medium text-gray-900">معلومات الحساب البنكي</h3>
                      
                      <div>
                        <label className="form-label">اسم البنك</label>
                        <input
                          type="text"
                          value={settings.bankName}
                          onChange={(e) => handleChange('bankName', e.target.value)}
                          placeholder="مثال: بنك الخرطوم"
                          className="form-input"
                        />
                      </div>

                      <div>
                        <label className="form-label">رقم الحساب</label>
                        <input
                          type="text"
                          value={settings.accountNumber}
                          onChange={(e) => handleChange('accountNumber', e.target.value)}
                          placeholder="مثال: 1234567890"
                          className="form-input"
                        />
                      </div>

                      <div>
                        <label className="form-label">اسم صاحب الحساب</label>
                        <input
                          type="text"
                          value={settings.accountName}
                          onChange={(e) => handleChange('accountName', e.target.value)}
                          placeholder="مثال: محمد أحمد"
                          className="form-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900">إعدادات الأمان</h2>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    لتغيير كلمة المرور أو معلومات الحساب، يرجى التواصل مع الدعم الفني.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">معلومات الحساب</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">البريد الإلكتروني:</span>
                        <span className="font-medium">{profile?.user?.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">رقم الهاتف:</span>
                        <span className="font-medium">{profile?.user?.phone}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">تاريخ التسجيل:</span>
                        <span className="font-medium">
                          {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('ar-SD') : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </VendorLayout>
  );
};

export default Settings;
